import { createContext, useContext, useEffect, useState } from "react";
import { authApi } from "../lib/api";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export const ROLES = {
  msl: { label: "Field MSL" },
  leadership: { label: "Medical Affairs Lead" },
  compliance: { label: "Compliance / PV" },
};

export const ROLE_HOME = { msl: "/today", leadership: "/command", compliance: "/compliance" };

export function AppProvider({ children }) {
  // user: undefined = checking session, null = signed out, object = signed in
  const [user, setUser] = useState(undefined);
  // view override — lets one login flip between MSL / Leadership / Compliance interfaces
  const [view, setView] = useState(() => localStorage.getItem("mai_view") || null);
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem("mai_dark");
    if (stored !== null) return stored === "1";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    authApi.me().then(setUser).catch(() => setUser(null));
    const expire = () => setUser(null);
    window.addEventListener("mai-auth-expired", expire);
    return () => window.removeEventListener("mai-auth-expired", expire);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("mai_dark", dark ? "1" : "0");
  }, [dark]);

  const login = async (email, password) => {
    const u = await authApi.login(email, password);
    setUser(u);
    setView(null);
    localStorage.removeItem("mai_view");
    return u;
  };

  const logout = async () => {
    try { await authApi.logout(); } catch { /* clear locally regardless */ }
    setUser(null);
    setView(null);
    localStorage.removeItem("mai_view");
  };

  const switchView = (v) => {
    setView(v);
    localStorage.setItem("mai_view", v);
  };

  // Demo workspace only: switch the signed-in identity between the seeded personas
  const switchPersona = async (p) => {
    const u = await authApi.demoLogin(p);
    localStorage.setItem("mai_persona", p);
    setView(null);
    localStorage.removeItem("mai_view");
    setUser(u);
    return u;
  };

  const isDemo = user?.tenant?.environment === "demo" || user?.tenant_id === "tenant-demo";

  const role = view || user?.role || "msl";
  const roleInfo = { label: ROLES[role]?.label || role, name: user?.name || "" };

  return (
    <AppContext.Provider value={{ user, login, logout, role, accountRole: user?.role, switchView, switchPersona, isDemo, roleInfo, dark, setDark }}>
      {children}
    </AppContext.Provider>
  );
}
